/**
 * Actycrea: map.esm.js (ES6)
 * MIT
 */

import { PREFIX } from '../helpers/config.js';
import { deepMerge } from '../helpers/helpers.js';
import { onActive } from '../helpers/events.js';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

/* =========================
 * Selectores con PREFIX + fallback legado
 * =======================*/
const SELECTOR              = `data-${PREFIX}-mygm`;
const SELECTOR_DATA         = `[${SELECTOR}]`;
const SELECTOR_DATA_MAP     = `[data-${PREFIX}-map]`;
const SELECTOR_DATA_PANEL   = `[data-${PREFIX}-map-panel]`;
const SELECTOR_DATA_OVERLAY = '[data-map-overlay]';

/* =========================
 * Config
 * =======================*/
const DEFAULTS = {
  options: {
    center: { lat: -34.397, lng: 150.644 },
    zoom: 6,
    mapTypeControl: false,
    zoomControl: false,
    scrollwheel: false,
    scaleControl: false,
    streetViewControl: false,
    rotateControl: false,
    tiltControl: false,
    fullscreenControl: false,
    keyboardShortcuts: false,
    disableDoubleClickZoom: false,
    disableDefaultUI: true,
    gestureHandling: 'cooperative',
    tilt: 0,
    heading: 0,
    mapId: window?.wphack?.gmaps?.mapid,
  },
  merge: {},
  bounds: true,
  zoomMax: 6,
  panOnActivate: false,
  initialActive: 0,
  items: null,
  itemsGlobalKey: 'ITEMS'
};

const MyColors = {
  white: '--bs-white',
  black: '--bs-black',
  primary: '--bs-primary',
  secondary: '--bs-secondary',
  warning: '--bs-warning',
  dark: '--bs-dark',
  medium: '--bs-medium',
  light: '--bs-light'
};

/* =========================
 * Loader (compat con wphack)
 * =======================*/
const MyLoader = {
  key: window?.wphack?.gmaps?.front,
  v: 'weekly',
  language: window?.wphack?.locale || 'en',
  libraries: [ 'marker' ]
};

/* =========================
 * Clase
 * =======================*/
export class MyGM {
  /**
   * @param {HTMLElement} element
   * @param {Object} config
   */
  constructor( element , config = {} ){
    if( !( element instanceof HTMLElement ) ) throw new Error( 'MyGM: element inválido' );

    this._config  = deepMerge( DEFAULTS , config );
    this._element = element;

    this._mapEl     = element.querySelector( SELECTOR_DATA_MAP );
    this._panelEl   = element.querySelector( SELECTOR_DATA_PANEL );
    this._overlayEl = element.querySelector( SELECTOR_DATA_OVERLAY );

    if( !this._mapEl ) return;

    this._mapEl.id = `MAP${PREFIX.toUpperCase()}${Math.floor( ( Math.random() * 1e6 ) + 1 )}`;

    // estado
    this._map         = null;
    this._markers     = [];
    this._bounds      = null;
    this._active      = -1;
    this._colors      = {};
    this._zoomMax     = Number( this._config.zoomMax ) || 6;
    this._items       = [];
    this._listeners   = [];

    Object.entries( MyColors ).forEach( ([ key , cssVar ]) => {
      this._colors[ key ] = getComputedStyle( document.documentElement ).getPropertyValue( cssVar ).trim();
    });

    this._options = deepMerge( this._config.options , this._config.merge );

    this._onLoad().catch( ( err ) => this._onError( err ) );
  }

  _enableZoomHint(){
    if( !( this._overlayEl instanceof HTMLElement ) ) return;

    const showHint = () => {
      this._overlayEl.classList.remove( 'is-unlocked' );
      this._overlayEl.setAttribute( 'aria-hidden' , 'false' );
    };

    const hideHint = () => {
      this._overlayEl.classList.add( 'is-unlocked' );
      this._overlayEl.setAttribute( 'aria-hidden' , 'true' );
    };

    const onKeyDown = ( ev ) => {
      if( ev.key === 'Control' || ev.ctrlKey ){
        hideHint();
      }
    };

    const onKeyUp = () => {
      showHint();
    };

    const onBlur = () => {
      showHint();
    };

    window.addEventListener( 'keydown' , onKeyDown );
    window.addEventListener( 'keyup' , onKeyUp );
    window.addEventListener( 'blur' , onBlur );

    this._listeners.push(
      { target: window, type: 'keydown', handler: onKeyDown },
      { target: window, type: 'keyup', handler: onKeyUp },
      { target: window, type: 'blur', handler: onBlur }
    );
  }

  // _enableOverlayZoomLock(){
  //   if( !( this._overlayEl instanceof HTMLElement ) || !( this._mapEl instanceof HTMLElement ) ) return;

  //   let ctrlPressed = false;

  //   const enableOverlay = () => {
  //     this._overlayEl.classList.remove( 'is-unlocked' );
  //     this._overlayEl.setAttribute( 'aria-hidden' , 'false' );
  //   };

  //   const disableOverlay = () => {
  //     this._overlayEl.classList.add( 'is-unlocked' );
  //     this._overlayEl.setAttribute( 'aria-hidden' , 'true' );
  //   };

  //   const onKeyDown = ( ev ) => {
  //     if( ev.key === 'Control' || ev.ctrlKey ){
  //       ctrlPressed = true;
  //       disableOverlay();
  //     }
  //   };

  //   const onKeyUp = ( ev ) => {
  //     if( ev.key === 'Control' || !ev.ctrlKey ){
  //       ctrlPressed = false;
  //       enableOverlay();
  //     }
  //   };

  //   const onWindowBlur = () => {
  //     ctrlPressed = false;
  //     enableOverlay();
  //   };

  //   const onOverlayWheel = ( ev ) => {
  //     if( !ctrlPressed ){
  //       ev.preventDefault();
  //       enableOverlay();
  //       return;
  //     }

  //     disableOverlay();
  //   };

  //   const onOverlayMouseEnter = () => {
  //     if( ctrlPressed ){
  //       disableOverlay();
  //     } else {
  //       enableOverlay();
  //     }
  //   };

  //   const onMapMouseLeave = () => {
  //     ctrlPressed = false;
  //     enableOverlay();
  //   };

  //   window.addEventListener( 'keydown' , onKeyDown );
  //   window.addEventListener( 'keyup' , onKeyUp );
  //   window.addEventListener( 'blur' , onWindowBlur );
  //   this._overlayEl.addEventListener( 'wheel' , onOverlayWheel , { passive: false } );
  //   this._overlayEl.addEventListener( 'mouseenter' , onOverlayMouseEnter );
  //   this._mapEl.addEventListener( 'mouseleave' , onMapMouseLeave );

  //   this._listeners.push(
  //     { target: window, type: 'keydown', handler: onKeyDown },
  //     { target: window, type: 'keyup', handler: onKeyUp },
  //     { target: window, type: 'blur', handler: onWindowBlur },
  //     { target: this._overlayEl, type: 'wheel', handler: onOverlayWheel },
  //     { target: this._overlayEl, type: 'mouseenter', handler: onOverlayMouseEnter },
  //     { target: this._mapEl, type: 'mouseleave', handler: onMapMouseLeave }
  //   );
  // }

  _enableControlledScroll(){
    if( !( this._mapEl instanceof HTMLElement ) ) return;

    let ctrlPressed = false;

    const onKeyDown = ( e ) => {
      if( e.ctrlKey ){
        ctrlPressed = true;
        this._map.setOptions({ scrollwheel: true });
      }
    };

    const onKeyUp = () => {
      ctrlPressed = false;
      this._map.setOptions({ scrollwheel: false });
    };

    const onWheel = ( e ) => {
      if( !ctrlPressed ){
        // bloquea zoom del mapa
        this._map.setOptions({ scrollwheel: false });
      }
    };

    window.addEventListener( 'keydown' , onKeyDown );
    window.addEventListener( 'keyup' , onKeyUp );
    this._mapEl.addEventListener( 'wheel' , onWheel , { passive: true });

    this._listeners.push(
      { target: window, type: 'keydown', handler: onKeyDown },
      { target: window, type: 'keyup', handler: onKeyUp },
      { target: this._mapEl, type: 'wheel', handler: onWheel }
    );
  }

  _initBounds(){
    if( this._config.bounds === true ) this._bounds = new this._LatLngBounds();
  }

  _fitBounds(){
    if( this._config.bounds !== true || !this._bounds || this._bounds.isEmpty() ) return;

    this._map.fitBounds( this._bounds );

    if( window.google?.maps?.event ){
      window.google.maps.event.addListenerOnce( this._map , 'bounds_changed' , () => {
        if( this._map.getZoom() > this._zoomMax ){
          this._map.setZoom( this._zoomMax );
        }
      });
    }
  }

  async _onLoad(){

    if( !MyLoader.key ){
      console.error( 'MyGM: key no definida' );
      return;
    }

    setOptions( MyLoader );

    const { Map } = await importLibrary( 'maps' );
    const { LatLng , LatLngBounds } = await importLibrary( 'core' );
    const { AdvancedMarkerElement } = await importLibrary( 'marker' );

    this._Map = Map;
    this._LatLng = LatLng;
    this._LatLngBounds = LatLngBounds;
    this._AdvancedMarkerElement = AdvancedMarkerElement;

    this._map = new this._Map( this._mapEl , this._options );

    this._enableControlledScroll();
    // this._enableOverlayZoomLock();
    this._enableZoomHint();

    this._initBounds();

    this._items = Array.isArray( this._config.items )
      ? this._config.items
      : (
          window?.[ this._config.itemsGlobalKey ] &&
          Array.isArray( window[ this._config.itemsGlobalKey ] )
            ? window[ this._config.itemsGlobalKey ]
            : []
        );

    this._items.forEach( ( item , index ) => this._addMarker( item , index ) );

    if( this._markers.length ){
      this._fitBounds();

      const initialIndex = this._sanitizeIndex( this._config.initialActive );
      if( initialIndex >= 0 ){
        this._activateByIndex( initialIndex , {
          updatePanel: true,
          pan: false,
          emit: false
        });
      }
    }

    this._bindEvents();
  }

  _bindEvents(){
    const onActiveHandler = ( ev ) => {
      const index = this._sanitizeIndex( ev?.detail?.index );
      if( index < 0 ) return;

      this._activateByIndex( index , {
        updatePanel: true,
        pan: !!this._config.panOnActivate,
        emit: false
      });
    };

    window.addEventListener( onActive , onActiveHandler );
    this._listeners.push({ target: window, type: onActive, handler: onActiveHandler });
  }

  _onError( error ){
    console.error( 'MyGM: loader error' , error );
  }

  /**
   * @param {number|string} value
   * @returns {number}
   */
  _sanitizeIndex( value ){
    const index = Number( value );
    if( !Number.isInteger( index ) ) return -1;
    if( index < 0 || index >= this._markers.length ) return -1;
    return index;
  }

  /**
   * @param {Object} item
   * @param {number} index
   */
  _addMarker( item , index ){
    const lat = Number( item?.location?.lat );
    const lng = Number( item?.location?.lng );

    if( !Number.isFinite( lat ) || !Number.isFinite( lng ) ) return;

    const pos = new this._LatLng( lat , lng );
    const content = this._buildMarkerContent( item , false );

    const marker = new this._AdvancedMarkerElement({
      map: this._map,
      position: pos,
      title: this._getItemTitle( item ),
      content,
      gmpClickable: true
    });

    marker.__data = {
      index,
      item
    };

    marker.addListener( 'click' , () => {
      this._activateByIndex( index , {
        updatePanel: true,
        pan: true,
        emit: true
      });
    });

    if( this._config.bounds === true && this._bounds ){
      this._bounds.extend( pos );
    }

    this._markers.push( marker );
  }

  /**
   * @param {Object} item
   * @param {boolean} active
   * @returns {HTMLElement}
   */
  _buildMarkerContent( item , active = false ){
    const root = document.createElement( 'button' );
    root.type = 'button';
    root.className = 'map-marker';
    root.setAttribute( 'aria-label' , this._getItemTitle( item ) );

    const color = item?.color || this._colors.primary;
    const activeColor = item?.activeColor || color;

    const headSize = Number( item?.headSize ) || 24;
    const stemWidth = Number( item?.stemWidth ) || 2;
    const stemHeight = Number( item?.stemHeight ) || 8;
    const dotSize = Number( item?.dotSize ) || 4;
    const ringSize = Number( item?.ringSize ) || ( headSize + 6 );

    root.dataset.defaultColor = color;
    root.dataset.activeColor = activeColor;
    root.dataset.headSize = String( headSize );
    root.dataset.stemWidth = String( stemWidth );
    root.dataset.stemHeight = String( stemHeight );
    root.dataset.dotSize = String( dotSize );
    root.dataset.ringSize = String( ringSize );

    const inner = document.createElement( 'span' );
    inner.className = 'map-marker__inner';

    const ring = document.createElement( 'span' );
    ring.className = 'map-marker__ring';

    const head = document.createElement( 'span' );
    head.className = 'map-marker__head';

    const stem = document.createElement( 'span' );
    stem.className = 'map-marker__stem';

    const dot = document.createElement( 'span' );
    dot.className = 'map-marker__dot';

    inner.append( ring , head , stem , dot );
    root.appendChild( inner );

    this._paintMarker( root , active );

    return root;
  }

  /**
   * @param {HTMLElement} el
   * @param {boolean} active
   */
  _paintMarker( el , active ){
    if( !( el instanceof HTMLElement ) ) return;

    const color = active ? el.dataset.activeColor : el.dataset.defaultColor;
    const headSize = Number( el.dataset.headSize ) || 108;
    const stemWidth = Number( el.dataset.stemWidth ) || 8;
    const stemHeight = Number( el.dataset.stemHeight ) || 118;
    const dotSize = Number( el.dataset.dotSize ) || 28;
    const ringSize = Number( el.dataset.ringSize ) || ( headSize + 24 );

    const ring = el.querySelector( '.map-marker__ring' );
    const head = el.querySelector( '.map-marker__head' );
    const stem = el.querySelector( '.map-marker__stem' );
    const dot = el.querySelector( '.map-marker__dot' );
    const inner = el.querySelector( '.map-marker__inner' );

    el.classList.toggle( 'is-active' , !!active );
    el.style.border = '0';
    el.style.padding = '0';
    el.style.margin = '0';
    el.style.background = 'transparent';
    el.style.cursor = 'pointer';
    el.style.outline = 'none';
    el.style.boxShadow = 'none';

    if( inner instanceof HTMLElement ){
      inner.style.position = 'relative';
      inner.style.display = 'block';
      inner.style.width = `${Math.max( ringSize , headSize )}px`;
      inner.style.height = `${headSize + stemHeight + dotSize}px`;
    }

    if( ring instanceof HTMLElement ){
      ring.style.position = 'absolute';
      ring.style.left = '50%';
      ring.style.top = '0';
      ring.style.width = `${ringSize}px`;
      ring.style.height = `${ringSize}px`;
      ring.style.borderRadius = '999px';
      ring.style.border = `1px solid ${color}`;
      ring.style.transform = 'translateX(-50%)';
      ring.style.boxSizing = 'border-box';
      ring.style.opacity = active ? '1' : '0';
      ring.style.transition = 'opacity .2s ease';
      ring.style.pointerEvents = 'none';
      ring.style.background = 'transparent';
    }

    if( head instanceof HTMLElement ){
      head.style.position = 'absolute';
      head.style.left = '50%';
      head.style.top = active ? `${( ringSize - headSize ) / 2}px` : '0';
      head.style.width = `${headSize}px`;
      head.style.height = `${headSize}px`;
      head.style.borderRadius = '999px';
      head.style.background = color;
      head.style.transform = 'translateX(-50%)';
      head.style.boxSizing = 'border-box';
    }

    if( stem instanceof HTMLElement ){
      stem.style.position = 'absolute';
      stem.style.left = '50%';
      stem.style.top = active ? `${( ringSize - headSize ) / 2 + headSize}px` : `${headSize}px`;
      stem.style.width = `${stemWidth}px`;
      stem.style.height = `${stemHeight}px`;
      stem.style.background = color;
      stem.style.transform = 'translateX(-50%)';
      stem.style.borderRadius = '0px';
    }

    if( dot instanceof HTMLElement ){
      dot.style.position = 'absolute';
      dot.style.left = '50%';
      dot.style.top = active
        ? `${( ringSize - headSize ) / 2 + headSize + stemHeight}px`
        : `${headSize + stemHeight}px`;
      dot.style.width = `${dotSize}px`;
      dot.style.height = `${dotSize}px`;
      dot.style.borderRadius = '999px';
      dot.style.background = color;
      dot.style.transform = 'translateX(-50%)';
    }
  }

  /**
   * @param {string} color
   * @param {number} alpha
   * @returns {string}
   */
  _hexToRgba( color , alpha = 1 ){
    if( !color || typeof color !== 'string' ) return `rgba(0,0,0,${alpha})`;

    const value = color.trim();

    if( value.startsWith( 'rgb(' ) ) return value.replace( 'rgb(' , 'rgba(' ).replace( ')' , `, ${alpha})` );
    if( value.startsWith( 'rgba(' ) ) return value;

    if( !value.startsWith( '#' ) ) return `rgba(0,0,0,${alpha})`;

    let hex = value.replace( '#' , '' );

    if( hex.length === 3 ){
      hex = hex.split( '' ).map( ( ch ) => ch + ch ).join( '' );
    }

    const int = parseInt( hex , 16 );
    if( Number.isNaN( int ) ) return `rgba(0,0,0,${alpha})`;

    const r = ( int >> 16 ) & 255;
    const g = ( int >> 8 ) & 255;
    const b = int & 255;

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  /**
   * @param {Object} item
   * @returns {string}
   */
  _getItemTitle( item ){
    return item?.title?.title || item?.title || item?.city || item?.name || '';
  }

  /**
   * @param {number} index
   * @param {Object} options
   */
  _activateByIndex( index , options = {} ){
    const safeIndex = this._sanitizeIndex( index );
    if( safeIndex < 0 ) return;

    const opts = {
      updatePanel: true,
      pan: false,
      emit: false,
      ...options
    };

    if( this._active === safeIndex ){
      if( opts.updatePanel ) this._renderPanel( safeIndex );
      return;
    }

    if( this._active >= 0 ){
      const prevMarker = this._markers[ this._active ];
      this._setMarkerState( prevMarker , false );
    }

    const marker = this._markers[ safeIndex ];
    this._setMarkerState( marker , true );
    this._active = safeIndex;

    if( opts.updatePanel ){
      this._renderPanel( safeIndex );
    }

    if( opts.pan && this._map?.panTo && marker?.position ){
      this._map.panTo( marker.position );
    }

    if( opts.emit ){
      window.dispatchEvent( new CustomEvent( `${PREFIX}:map-active` , {
        detail: {
          index: safeIndex,
          item: this._items[ safeIndex ] || null
        }
      }));
    }
  }

  /**
   * @param {AdvancedMarkerElement} marker
   * @param {boolean} active
   */
  _setMarkerState( marker , active ){
    if( !marker?.content ) return;

    this._paintMarker( marker.content , active );
    marker.zIndex = active ? 100 : 1;
  }

  /**
   * @param {number} index
   */
  _renderPanel( index ){
    if( !( this._panelEl instanceof HTMLElement ) ) return;

    const item = this._items?.[ index ];
    if( !item ) return;

    this._fillByDataAttr( 'image' , item?.image || item?.image?.url || '' , { type: 'src' } );
    this._fillByDataAttr( 'title' , this._getItemTitle( item ) );
    this._fillByDataAttr( 'subtitle' , item?.subtitle || '' );
    this._fillByDataAttr( 'country' , item?.country || '' );
    this._fillByDataAttr( 'city' , item?.city || '' );
    this._fillByDataAttr( 'address' , item?.address || '' );
    this._fillByDataAttr( 'address2' , item?.address2 || '' );
    this._fillByDataAttr( 'manager-label' , item?.managerLabel || '' );
    this._fillByDataAttr( 'manager' , item?.manager || item?.name || '' );

    this._fillByDataAttr( 'phone' , item?.phone || '' , {
      href: item?.phoneUrl || ( item?.phone ? `tel:${String( item.phone ).replace(/\s+/g , '')}` : '' )
    });

    this._fillByDataAttr( 'email' , item?.email || '' , {
      href: item?.emailUrl || ( item?.email ? `mailto:${item.email}` : '' )
    });

    this._fillByDataAttr( 'linkedin' , item?.linkedinLabel || '' , {
      href: item?.linkedinUrl || item?.linkedin || '',
      keepTextOnEmptyValue: true,
      keepExistingText: true
    });

    this._panelEl.dispatchEvent( new CustomEvent( `${PREFIX}:map-panel-update` , {
      detail: { index, item }
    }));
  }

  /**
   * @param {string} key
   * @param {string} value
   * @param {Object} options
   * @param {'text'|'src'} [options.type='text']
   * @param {string} [options.href='']
   * @param {boolean} [options.keepTextOnEmptyValue=false]
   * @param {boolean} [options.keepExistingText=false]
   */
  _fillByDataAttr( key , value , options = {} ){
    const node = this._panelEl.querySelector( `[data-map-field="${key}"]` );
    if( !( node instanceof HTMLElement ) ) return;

    const opts = {
      type: 'text',
      href: '',
      keepTextOnEmptyValue: false,
      keepExistingText: false,
      ...options
    };

    const finalValue = value ?? '';

    if( opts.type === 'src' ){
      if( node.tagName === 'IMG' ){
        if( finalValue ){
          node.setAttribute( 'src' , finalValue );
          node.removeAttribute( 'hidden' );
        } else {
          node.removeAttribute( 'src' );
          node.setAttribute( 'hidden' , 'hidden' );
        }
      }
      return;
    }

    if( 'href' in opts ){
      if( opts.href ){
        node.setAttribute( 'href' , opts.href );
      } else {
        node.removeAttribute( 'href' );
      }
    }

    if( !opts.keepExistingText ){
      node.textContent = finalValue;
    } else if( finalValue ) {
      node.textContent = finalValue;
    }

    const hasVisibleContent = !!finalValue || !!opts.href || opts.keepTextOnEmptyValue;

    if( hasVisibleContent ){
      node.removeAttribute( 'hidden' );
    } else {
      node.setAttribute( 'hidden' , 'hidden' );
    }
  }

  /* ============
   * API pública mínima
   * ===========*/
  getMap(){ return this._map; }
  getMarkers(){ return this._markers.slice(); }
  getItems(){ return this._items.slice(); }
  getActiveIndex(){ return this._active; }

  /**
   * @param {number} index
   * @param {boolean} pan
   */
  setActive( index , pan = false ){
    this._activateByIndex( index , {
      updatePanel: true,
      pan,
      emit: false
    });
  }

  destroy(){
    this._listeners.forEach( ({ target , type , handler }) => {
      target.removeEventListener( type , handler );
    });
    this._listeners = [];

    this._markers.forEach( ( marker ) => {
      if( marker ){
        marker.map = null;
      }
    });

    this._markers = [];
    this._map = null;
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ){
    const nodes = root.querySelectorAll( SELECTOR_DATA );
    const instances = [];

    nodes.forEach( ( el ) => {
      if( el._mygmInstance ) return;

      let cfg = {};
      const raw = el.getAttribute( SELECTOR );

      if( raw ){
        try {
          cfg = JSON.parse( raw );
        } catch( _ ){
          cfg = {};
        }
      }

      el._mygmInstance = new MyGM( el , cfg );
      instances.push( el._mygmInstance );
    });

    return instances;
  }
}

export default MyGM;